import { NgModule } from '@angular/core';
import { provideHttpClient } from '@angular/common/http';
import { APOLLO_OPTIONS, Apollo as BaseApollo } from 'apollo-angular';
import { ApolloClientOptions, ApolloLink, InMemoryCache } from '@apollo/client/core';
import { CombinedGraphQLErrors } from '@apollo/client/errors';
import UploadHttpLink from 'apollo-upload-client/UploadHttpLink.mjs'; // Import UploadHttpLink from apollo-upload-client
import { onError } from '@apollo/client/link/error'; // Handle errors
import { setContext } from '@apollo/client/link/context'; // For setting authentication context
import { environment } from '../environments/environment'; // Your environment file for GraphQL API URL
import { AuthService } from './auth/auth.service'; // Your authentication service
import { SnackBarService } from './shared/components/snack-bar/snack-bar.service'; // Your snackbar service
import { AdminUserTenant } from './shared/api/types/GraphQL';
import { Apollo } from './shared/services/apollo-wrapper.service'; // Custom Apollo wrapper

const uri = environment.propeller.api.url; // Set GraphQL API URL

/**
 * The Authorization header for a skipAuth request — or NO header at all when
 * there is no token, which is the logged-out case.
 *
 * Never send it empty there. The gateway treats any Authorization header as a
 * presented credential, so `Authorization: ''` is rejected with 401 "the token
 * is not a valid identity token" before the request reaches a resolver — while
 * the identical request with the header OMITTED is served on the apikey +
 * tenant alone. That silently killed the one mutation we make while logged
 * out, the sign-in page's password reset: its handlers treat success and
 * failure identically (anti-enumeration), so the UI said "check your inbox"
 * and no email was ever sent. Measured against staging both ways (NBO-2387).
 *
 * Deliberately scoped to the skipAuth branch. The normal branch rethrows when
 * it cannot get a token or a tenant, so its token is never null in practice —
 * and if that ever changed, an empty header is the SAFER failure there: it is
 * refused at the gateway (401), whereas omitting it lets the request reach the
 * service, which refuses it one layer later (403 Forbidden resource, no data).
 * Both fail closed, but the edge is the better place to stop it.
 */
function skipAuthBearerHeader(token: string | null): { Authorization?: string } {
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export function createApollo(authService: AuthService, snackBarService: SnackBarService): ApolloClientOptions {
  const auth = setContext(async (_, { headers, skipAuth }) => {
    // If skipAuth is true, still get token but skip tenant from auth service
    if (skipAuth) {
      let token: string | null = null;
      try {
        token = await authService.getToken();
      } catch (error) {
        console.log('Could not get token for skipAuth query:', error);
      }

      return {
        headers: {
          apikey: environment.propeller.api.key,
          ...headers,
          ...skipAuthBearerHeader(token)
        }
      };
    }

    let token: string | null = null;
    let tenant: AdminUserTenant | null = null;

    try {
      token = await authService.getToken();
      tenant = await authService.getActiveTenant();

      if (!tenant) {
        throw new Error('No tenant available');
      }
    } catch (error) {
      throw error;
    }

    // For normal queries (not skipAuth), use tenant from auth service
    const finalTenant = tenant?.tenant || tenant?.id || '';

    // Separate the tenant from other headers to avoid conflicts
    const { tenant: contextTenant, ...otherHeaders } = headers || {};

    // Use contextTenant if provided, otherwise use finalTenant from auth service
    const tenantToUse = contextTenant || finalTenant;

    const authHeaders = {
      apikey: environment.propeller.api.key,
      tenant: tenantToUse,
      ...otherHeaders,
      Authorization: token ? `Bearer ${token}` : ''
    };
    return {
      headers: authHeaders
    };
  });

  const error = onError(({ error }) => {
    if (CombinedGraphQLErrors.is(error)) {
      error.errors.forEach(({ message, locations, path }) => {});
    } else {
      // Network error
    }
  });

  const link = ApolloLink.from([auth, error, new UploadHttpLink({ uri })]);
  // NBO-1811: previous Query.products / Query.product / Query.valuesetItems
  // policies set keyArgs: false + merge: true, collapsing every paginated and
  // filtered call into a single cache slot. Removed so Apollo's default keying
  // (by all input arguments) applies — distinct (filter, sort, page) tuples
  // get distinct cache entries. Escalate to per-(filter, sort) keyArgs +
  // accumulator merge only if profiling shows memory pressure.
  // Pricesheet nested-collection merges are kept: replace-on-fetch is correct
  // for paged sub-collections under a single Pricesheet entity.
  const cache = new InMemoryCache({
    // NBO-2219: the TenderChildItems fragment is declared on the ITenderBaseItem
    // interface. Without possibleTypes Apollo can't confirm a stored TenderBaseItem
    // satisfies that type condition, so the cache read-back after a cache-and-network
    // tender load stripped every childItem field except __typename — cluster option
    // rows rendered nameless on order reload.
    possibleTypes: {
      ITenderBaseItem: ['TenderMainItem', 'TenderBaseItem'],
      // NBO-2349: the attrValue fragment is declared on the AttributeValue interface.
      // Company/contact attribute columns only survived unregistered because those
      // listings run no-cache; any cache-mediated query selecting attrValue (the
      // orders listings are cache-and-network) hits the same NBO-2219 stripping.
      AttributeValue: [
        'AttributeTextValue',
        'AttributeEnumValue',
        'AttributeIntValue',
        'AttributeDecimalValue',
        'AttributeColorValue',
        'AttributeDateTimeValue'
      ]
    },
    typePolicies: {
      // NBO-2129: Tender has no `id`/`_id`, so Apollo stores it inline under the
      // `tender` root field keyed by ALL args. The editor open calls
      // getTender({orderId}) while the process flows call
      // getTender({orderId, revisionsPage, revisionsOffset}) — without keyArgs
      // those land in different cache entries and a read-only re-open / post-process
      // remount would miss the cache. Keying on orderId/tenderId only lets revisions
      // pagination share one entry. (Same typePolicy theme as NBO-2123.)
      Query: {
        fields: {
          tender: {
            keyArgs: ['orderId', 'tenderId']
          }
        }
      },
      // NBO-2129 follow-up: tender line items carry a deprecated non-unique `id`
      // (the backend returns 0 for every line), so default `__typename:id` keying
      // collapses all of an order's lines into one normalized entity and every row
      // renders as the same product. They're owned by their tender and never shared
      // across queries — store them inline instead of normalizing.
      TenderMainItem: { keyFields: false },
      TenderBaseItem: { keyFields: false },
      Pricesheet: {
        fields: {
          companies: {
            merge: (existing = [], incoming) => incoming
          },
          contacts: {
            merge: (existing = [], incoming) => incoming
          },
          customers: {
            merge: (existing = [], incoming) => incoming
          }
        }
      }
    }
  });

  return {
    link,
    cache
  };
}

@NgModule({
  providers: [
    provideHttpClient(),
    {
      provide: APOLLO_OPTIONS,
      useFactory: createApollo,
      deps: [AuthService, SnackBarService]
    },
    // Provide our custom Apollo wrapper as the Apollo service
    {
      provide: BaseApollo,
      useClass: Apollo
    }
  ]
})
export class GraphQLModule {}
